import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { ArrowLeft, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "Fellowship of the Royal College of Physicians (FRCP)",
    content: `Fellowship of the Royal College of Physicians (FRCP) is a prestigious honour awarded to physicians who have made a significant contribution to medicine, research, education, or the wider profession. It is distinct from MRCP (Membership), which is a postgraduate examination required for completion of rheumatology training.

FRCP criteria:
• Minimum 10 years post-registration (usually more in practice)
• Significant contribution to clinical medicine, medical research, education, or the profession
• Usually requires active MRCP membership
• Nomination by two existing Fellows (FRCPs)
• Assessment by the Fellowship Committee

There are three pathways: Recognition of Distinction (for exceptional clinical, academic, or educational contribution), Standard Election (nomination-based for sustained career contribution), and Honorary Fellowship (for non-physicians who have contributed to medicine).

Early career rheumatologists should focus on building the evidence base for FRCP — this means publications, teaching recognition, leadership roles, significant audit or QIP impact, and external professional recognition (BSR committee roles, NICE advisory roles, royal college committee membership).`,
  },
  {
    title: "BSR Fellowship",
    content: `The British Society for Rheumatology (BSR) elects Fellows of the BSR (FBSR) in recognition of distinguished contribution to rheumatology. This is an honour separate from FRCP and is awarded by the BSR Fellowship Committee.

Eligibility:
• Active BSR membership for at least 5 years
• Significant contribution to rheumatology in one or more of: clinical practice, research, education, leadership, or advocacy
• Nomination by two existing FBSR

The BSR Annual Meeting includes a Fellowship induction ceremony. FBSR is particularly valued for those who have contributed to BSR governance (council, working groups, SIGs), national guideline development, or educational programmes.

Early career rheumatologists can begin building their BSR profile through: joining working groups and Special Interest Groups (SIGs), contributing to BSR educational events, volunteering for guideline development, and attending the annual meeting consistently.`,
  },
  {
    title: "EULAR Schools of Rheumatology",
    content: `EULAR (European Alliance of Associations for Rheumatology) runs Schools of Rheumatology — intensive 3-5 day residential training courses for trainees and early career consultants across Europe.

Key schools include:
• EULAR School of Rheumatology: Musculoskeletal Ultrasound (practical, highly competitive)
• EULAR Epidemiology School — evidence-based rheumatology, study design, statistics
• EULAR School of Rheumatology: Clinical Immunology
• EULAR School of Rheumatology: Health Professionals — open to non-medical rheumatology professionals

Applications open annually (usually February-March) on the EULAR website. Bursaries are available for trainees from eligible countries, including the UK. The courses are taught by European experts and offer excellent networking opportunities.

EULAR Congress attendance (May/June, alternating European cities) is strongly recommended from ST4 onwards. Abstract submission, poster presentation, and oral communication slots are open to trainees. The Young EULAR network offers additional support and networking.`,
  },
  {
    title: "International Fellowships",
    content: `International fellowships offer unique opportunities to develop subspecialty expertise, build global networks, and strengthen your academic profile. Options for UK rheumatology trainees include:

• Hospital for Special Surgery (HSS), New York: competitive clinical fellowships in rheumatology and autoimmune disease, typically 6-12 months post-CCT
• Brigham and Women's Hospital / Mass General, Boston: lupus, vasculitis, and autoimmunity fellowships
• NIH (Bethesda): research fellowships via the Clinical Center rheumatology branch
• Mayo Clinic: subspecialty observerships and research attachments
• Canada (Toronto, Montreal): RA, SpA, and imaging fellowships

For UK trainees, a period abroad is usually post-CCT or during a research fellowship. GMC requires notification for periods >3 months abroad if registered with a UK employer. JRCPTB permits out-of-programme experience (OOPE) for overseas attachments during training — apply 6-12 months in advance via your Deanery.`,
  },
];

export default function Fellowships() {
  return (
    <DoctorLayout title="Fellowships & Honours">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/doctor/career">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Career Pathways
            </Button>
          </Link>
        </div>

        <div className="bg-gradient-to-br from-amber-700 to-amber-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Award className="h-8 w-8 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">Fellowships & Professional Honours</h2>
              <p className="text-amber-300 text-sm">FRCP · BSR Fellowship · EULAR Schools · International opportunities</p>
            </div>
          </div>
          <p className="text-amber-200 text-sm leading-relaxed">
            Professional fellowships and honours recognise distinguished contribution to rheumatology. Building the evidence for FRCP or FBSR is a long-term endeavour — this guide outlines what each involves and how to position yourself for recognition.
          </p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
              <h3 className="text-lg font-bold text-slate-900">{section.title}</h3>
              <div className="space-y-3">
                {section.content.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-2">
          <h4 className="font-bold text-slate-900">Key Links</h4>
          <ul className="space-y-1 text-sm">
            <li><a href="https://www.rcplondon.ac.uk/fellowship" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">RCP Fellowship — Royal College of Physicians</a></li>
            <li><a href="https://www.rheumatology.org.uk/members/bsr-fellowship" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">BSR Fellowship</a></li>
            <li><a href="https://www.eular.org/edu_schools_of_rheumatology.cfm" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">EULAR Schools of Rheumatology</a></li>
            <li><a href="https://www.hss.edu/education_fellowships.asp" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">Hospital for Special Surgery Fellowships (HSS)</a></li>
          </ul>
        </div>
      </div>
    </DoctorLayout>
  );
}
